/**
 * BulkAddUsersModal
 * 
 * Modal for authoritatively syncing a provider roster from CSV.
 * Supports CSV paste or file upload for user data input.
 */

import { AlertCircle, CheckCircle, FileText, Loader2, Upload, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import httpService from "../../services/httpService";
import { subscriptionService } from "../../services/subscriptionService";
import usersService, { BulkUserEntry } from "../../services/usersService";
import { SubscriptionWithRelations } from "../../types/subscription";
import { CsvParseResult, parseUsersCsv, readFileAsText } from "../../utils/csvParser";
import AdminModalShell from "./AdminModalShell";

// ============================================================
// Types
// ============================================================

interface Provider {
  id: number;
  name: string;
}

interface BulkAddUsersModalProps {
  appearance?: "default" | "alpha";
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialProviderId?: number | null;
  lockProvider?: boolean;
}

type InputMode = "paste" | "upload";

// ============================================================
// Component
// ============================================================

const BulkAddUsersModal: React.FC<BulkAddUsersModalProps> = ({
  appearance = "default",
  isOpen,
  onClose,
  onSuccess,
  initialProviderId = null,
  lockProvider = false,
}) => {
  const sessionRef = useRef(0);
  const fileRequestRef = useRef(0);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Input mode state
  const [inputMode, setInputMode] = useState<InputMode>("paste");
  const [csvContent, setCsvContent] = useState("");
  const [fileName, setFileName] = useState<string | null>(null);

  // Parsed CSV state
  const [parseResult, setParseResult] = useState<CsvParseResult | null>(null);

  // Provider state
  const [providers, setProviders] = useState<Provider[]>([]);
  const [selectedProviderId, setSelectedProviderId] = useState<number | null>(null);
  const [loadingProviders, setLoadingProviders] = useState(false);

  const [providerSubscribers, setProviderSubscribers] = useState<SubscriptionWithRelations[]>([]);
  const [loadingRoster, setLoadingRoster] = useState(false);
  const [rosterError, setRosterError] = useState<string | null>(null);

  const [confirmRosterSync, setConfirmRosterSync] = useState(false);

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState<{ message: string; count: number } | null>(null);

  // ============================================================
  // Effects
  // ============================================================

  // Reset state when modal opens/closes
  useEffect(() => {
    const invalidate = () => { ++sessionRef.current; ++fileRequestRef.current; };
    invalidate();
    setSubmitting(false);
    if (isOpen) {
      setCsvContent("");
      setFileName(null);
      setParseResult(null);
      setSelectedProviderId(initialProviderId);
      setConfirmRosterSync(false);
      setProviderSubscribers([]);
      setLoadingRoster(false);
      setRosterError(null);
      setSubmitError(null);
      setSubmitSuccess(null);
      setInputMode("paste");
      fetchProviders();
    }
    return () => { invalidate(); if (closeTimerRef.current) clearTimeout(closeTimerRef.current); };
  }, [isOpen, initialProviderId]);

  // Parse CSV when content changes
  useEffect(() => {
    if (csvContent.trim()) {
      const result = parseUsersCsv(csvContent);
      setParseResult(result);
    } else {
      setParseResult(null);
    }
  }, [csvContent]);

  // Fetch provider-scoped data when provider is selected
  useEffect(() => {
    setConfirmRosterSync(false);
    setSubmitError(null);
    setProviderSubscribers([]);
    setRosterError(null);

    if (!selectedProviderId) {
      setLoadingRoster(false);
      return;
    }

    let active = true;
    const providerId = selectedProviderId;

    const loadRoster = async () => {
      try {
        setLoadingRoster(true);
        const subscribers = await subscriptionService.listProviderSubscribers(providerId);

        if (!active) return;
        setProviderSubscribers(subscribers);
      } catch (err) {
        console.error("Failed to fetch provider roster:", err);
        if (active) {
          setProviderSubscribers([]);
          setRosterError("Failed to load the current roster. Refresh before syncing.");
        }
      } finally {
        if (active) setLoadingRoster(false);
      }
    };

    loadRoster();

    return () => {
      active = false;
    };
  }, [selectedProviderId]);

  useEffect(() => {
    setConfirmRosterSync(false);
  }, [csvContent, selectedProviderId]);

  // ============================================================
  // Data Fetching
  // ============================================================

  const fetchProviders = async () => {
    const sessionId = sessionRef.current;
    try {
      setLoadingProviders(true);
      const response = await httpService.get<{ providers: Provider[] }>("/providers");
      if (sessionRef.current === sessionId) setProviders(response.data.providers || []);
    } catch (err) {
      console.error("Failed to fetch providers:", err);
    } finally {
      if (sessionRef.current === sessionId) setLoadingProviders(false);
    }
  };

  // ============================================================
  // Handlers
  // ============================================================

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file extension (accept attribute is just a hint)
    if (!file.name.toLowerCase().endsWith(".csv")) {
      setSubmitError("Please upload a CSV file");
      return;
    }

    const requestId = ++fileRequestRef.current;
    try {
      const content = await readFileAsText(file);
      if (requestId !== fileRequestRef.current) return;
      setCsvContent(content);
      setFileName(file.name);
      setSubmitError(null);
    } catch (err) {
      if (requestId !== fileRequestRef.current) return;
      console.error("Failed to read file:", err);
      setSubmitError("Failed to read file");
    }
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".csv")) {
      setSubmitError("Please upload a CSV file");
      return;
    }

    const requestId = ++fileRequestRef.current;
    try {
      const content = await readFileAsText(file);
      if (requestId !== fileRequestRef.current) return;
      setCsvContent(content);
      setFileName(file.name);
      setInputMode("upload");
    } catch (err) {
      if (requestId !== fileRequestRef.current) return;
      console.error("Failed to read file:", err);
      setSubmitError("Failed to read file");
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const validUsers = useMemo(() => {
    return parseResult?.users.filter(u => u.isValid) || [];
  }, [parseResult]);

  const uploadedEmailSet = useMemo(() => {
    return new Set(validUsers.map((user) => user.email.trim().toLowerCase()));
  }, [validUsers]);

  const omittedSubscribers = useMemo(() => {
    if (!selectedProviderId || uploadedEmailSet.size === 0) return [];
    return providerSubscribers.filter((subscription) => {
      const email = subscription.subscriptionUser.email.trim().toLowerCase();
      return !uploadedEmailSet.has(email);
    });
  }, [providerSubscribers, selectedProviderId, uploadedEmailSet]);

  const isFormValid = useMemo(() => {
    if (validUsers.length === 0) return false;
    if (!selectedProviderId) return false;
    if (loadingRoster || rosterError) return false;
    if (omittedSubscribers.length > 0 && !confirmRosterSync) return false;
    return true;
  }, [
    validUsers,
    selectedProviderId,
    loadingRoster,
    rosterError,
    omittedSubscribers,
    confirmRosterSync,
  ]);

  // ============================================================
  // Submit
  // ============================================================

  const handleSubmit = async () => {
    if (!isFormValid || !selectedProviderId) return;

    const sessionId = sessionRef.current;
    setSubmitting(true);
    setSubmitError(null);
    setSubmitSuccess(null);

    try {
      const users: BulkUserEntry[] = validUsers.map(u => ({
        email: u.email,
        name: u.name,
        providerSubscriptions: [{
          providerId: selectedProviderId,
          baskets: [],
          portfolios: [],
        }],
      }));

      const response = await usersService.bulkUpdateInvestors({ users });
      if (sessionRef.current !== sessionId) return;
      
      setSubmitSuccess({
        message: response.message,
        count: response.usersProcessed,
      });

      // Auto-close after success and refresh parent
      closeTimerRef.current = setTimeout(() => {
        if (sessionRef.current !== sessionId) return;
        onSuccess();
        onClose();
      }, 2000);
    } catch (err) {
      if (sessionRef.current !== sessionId) return;
      console.error("Roster sync error:", err);
      const message = err instanceof Error ? err.message : "Failed to sync roster";
      setSubmitError(message);
    } finally {
      if (sessionRef.current === sessionId) setSubmitting(false);
    }
  };

  // ============================================================
  // Render
  // ============================================================

  if (!isOpen) return null;

  return (
    <AdminModalShell appearance={appearance}
      isOpen={isOpen}
      onClose={onClose}
      title="Sync Provider Roster"
      description="Paste or upload the provider roster CSV for the selected provider."
      maxWidthClassName="max-w-2xl"
      closeDisabled={submitting}
      footer={
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className={appearance === "alpha" ? "text-sm text-v7-ink/60" : "text-sm text-gray-500 dark:text-gray-400"}>
            {validUsers.length > 0 && (
              <>
                {validUsers.length} user{validUsers.length !== 1 ? "s" : ""} in roster file
                {parseResult && parseResult.invalidCount > 0 && (
                  <span className={appearance === "alpha" ? "ml-2 text-v7-bronze-dark" : "ml-2 text-rp-amber"}>
                    ({parseResult.invalidCount} invalid row{parseResult.invalidCount !== 1 ? "s" : ""} skipped)
                  </span>
                )}
              </>
            )}
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className={appearance === "alpha" ? "rounded-v7-panel border border-v7-ink/15 bg-v7-white px-4 py-2 text-sm font-semibold text-v7-ink-soft transition-colors hover:border-v7-ink/25 disabled:opacity-50" : "rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-300 disabled:opacity-50 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!isFormValid || submitting || !!submitSuccess}
              className={appearance === "alpha" ? "flex items-center rounded-v7-panel bg-v7-ink px-4 py-2 text-sm font-semibold text-v7-white transition-colors hover:bg-v7-bronze-dark disabled:cursor-not-allowed disabled:opacity-50" : "flex items-center rounded-lg bg-rp-amber px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-yellow-600 disabled:cursor-not-allowed disabled:opacity-50"}
            >
              {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sync Roster
            </button>
          </div>
        </div>
      }
    >

          {/* Content */}
            {/* Success Message */}
            {submitSuccess && (
              <div className={appearance === "alpha" ? "mb-4 flex items-center rounded-v7-panel border border-v7-success/30 bg-v7-success/10 p-4 text-sm text-v7-success" : "mb-4 flex items-center rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400"}>
                <CheckCircle className="w-5 h-5 mr-2" />
                <span>{submitSuccess.count} users processed successfully</span>
              </div>
            )}

            {/* CSV Input Section */}
            <div className="mb-6">
              <label className={appearance === "alpha" ? "mb-2 block text-sm font-medium text-v7-ink-soft" : "mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"}>
                User Data (CSV) *
              </label>

              {/* Input Mode Tabs */}
              <div className={appearance === "alpha" ? "mb-3 inline-flex rounded-v7-panel border border-v7-ink/15 bg-v7-white p-0.5" : "mb-3 inline-flex rounded-lg border border-gray-200 bg-white p-0.5 dark:border-rp-separator dark:bg-rp-surface"}>
                <button
                  type="button"
                  onClick={() => setInputMode("paste")}
                  className={appearance === "alpha"? `rounded-v7-control px-4 py-1.5 text-sm font-medium transition-colors ${inputMode ==="paste"
    ? "bg-v7-ink text-v7-white" : "text-v7-ink/60 hover:text-v7-ink-soft"}` : `rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                    inputMode === "paste"
                      ? "bg-rp-amber text-white"
                      : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  }`}
                >
                  Paste CSV
                </button>
                <button
                  type="button"
                  onClick={() => setInputMode("upload")}
                  className={appearance === "alpha"? `rounded-v7-control px-4 py-1.5 text-sm font-medium transition-colors ${inputMode ==="upload"
    ? "bg-v7-ink text-v7-white" : "text-v7-ink/60 hover:text-v7-ink-soft"}` : `rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
                    inputMode === "upload"
                      ? "bg-rp-amber text-white"
                      : "text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                  }`}
                >
                  Upload File
                </button>
              </div>

              {inputMode === "paste" ? (
                <textarea
                  value={csvContent}
                  onChange={(e) => {
                    setCsvContent(e.target.value);
                    setFileName(null);
                  }}
                  placeholder="email,name&#10;john@example.com,John Smith&#10;jane@example.com,Jane Doe"
                  rows={6}
                  className={appearance === "alpha" ? "w-full rounded-v7-panel border border-v7-ink/15 bg-v7-white px-3 py-2 font-mono text-sm text-v7-ink placeholder-v7-ink/45 transition-all focus:border-v7-bronze/30 focus:outline-none focus:ring-1 focus:ring-v7-bronze-dark" : "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 font-mono text-sm text-gray-900 placeholder-gray-400 transition-all focus:border-rp-amber focus:outline-none focus:ring-1 focus:ring-rp-amber dark:border-rp-separator dark:bg-rp-surface dark:text-white dark:placeholder-gray-500"}
                />
              ) : (
                <div
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  className={appearance === "alpha" ? "rounded-v7-panel border-2 border-dashed border-v7-ink/25 p-6 text-center transition-colors hover:border-v7-bronze/30" : "rounded-lg border-2 border-dashed border-gray-300 p-6 text-center transition-colors hover:border-rp-amber dark:border-rp-separator dark:hover:border-rp-amber"}
                >
                  {fileName ? (
                    <div className="flex items-center justify-center space-x-2">
                      <FileText className={appearance === "alpha" ? "h-5 w-5 text-v7-success" : "h-5 w-5 text-green-600 dark:text-green-500"} />
                      <span className={appearance === "alpha" ? "text-sm text-v7-ink-soft" : "text-sm text-gray-700 dark:text-gray-300"}>{fileName}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setCsvContent("");
                          setFileName(null);
                        }}
                        className={appearance === "alpha" ? "rounded-v7-panel p-1 text-v7-ink/60 transition-colors hover:bg-v7-paper hover:text-v7-ink-soft" : "rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-rp-surface dark:hover:text-gray-300"}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <>
                      <Upload className={appearance === "alpha" ? "mx-auto mb-2 h-8 w-8 text-v7-ink/60" : "mx-auto mb-2 h-8 w-8 text-gray-400 dark:text-gray-500"} />
                      <p className={appearance === "alpha" ? "text-sm text-v7-ink/60" : "text-sm text-gray-500 dark:text-gray-400"}>
                        Drag and drop a CSV file, or
                      </p>
                      <label className="mt-2 inline-block">
                        <span className={appearance === "alpha" ? "cursor-pointer text-sm font-medium text-v7-bronze-dark hover:text-v7-bronze-dark" : "cursor-pointer text-sm font-medium text-rp-amber hover:text-yellow-600"}>
                          browse to upload
                        </span>
                        <input
                          type="file"
                          accept=".csv"
                          onChange={handleFileChange}
                          className="hidden"
                        />
                      </label>
                    </>
                  )}
                </div>
              )}

              {/* Parse Result Summary */}
              {parseResult && (
                <div className={appearance === "alpha" ? "mt-2 text-sm text-v7-ink/60" : "mt-2 text-sm text-gray-500 dark:text-gray-400"}>
                  {parseResult.validCount} valid, {parseResult.invalidCount} invalid
                  {parseResult.hasHeader && " (header detected)"}
                </div>
              )}
            </div>

            {/* Preview Table */}
            {parseResult && parseResult.users.length > 0 && (
              <div className="mb-6">
                <label className={appearance === "alpha" ? "mb-2 block text-sm font-medium text-v7-ink-soft" : "mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"}>
                  Preview ({parseResult.users.length} rows)
                </label>
                <div className={appearance === "alpha" ? "max-h-40 overflow-y-auto rounded-v7-panel border border-v7-ink/15" : "max-h-40 overflow-y-auto rounded-lg border border-gray-200 dark:border-rp-separator"}>
                  <table className={appearance === "alpha" ? "min-w-full divide-y divide-v7-ink/15" : "min-w-full divide-y divide-gray-200 dark:divide-gray-700"}>
                    <thead className={appearance === "alpha" ? "bg-v7-paper" : "bg-gray-50 dark:bg-rp-surface-sunken"}>
                      <tr>
                        <th className={appearance === "alpha" ? "px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-v7-ink/60" : "px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"}>
                          Row
                        </th>
                        <th className={appearance === "alpha" ? "px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-v7-ink/60" : "px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"}>
                          Email
                        </th>
                        <th className={appearance === "alpha" ? "px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-v7-ink/60" : "px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"}>
                          Name
                        </th>
                        <th className={appearance === "alpha" ? "px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-v7-ink/60" : "px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400"}>
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className={appearance === "alpha" ? "divide-y divide-v7-ink/15 bg-v7-white" : "divide-y divide-gray-200 bg-white dark:divide-rp-separator dark:bg-black"}>
                      {parseResult.users.slice(0, 10).map((user) => (
                        <tr key={user.rowNumber} className={appearance === "alpha" ? "transition-colors hover:bg-v7-paper" : "transition-colors hover:bg-gray-50 dark:hover:bg-rp-surface"}>
                          <td className={appearance === "alpha" ? "px-3 py-2 text-sm text-v7-ink/60" : "px-3 py-2 text-sm text-gray-500 dark:text-gray-400"}>
                            {user.rowNumber}
                          </td>
                          <td className={appearance === "alpha" ? "px-3 py-2 font-mono text-sm text-v7-ink-soft" : "px-3 py-2 font-mono text-sm text-gray-700 dark:text-gray-300"}>
                            {user.email || "-"}
                          </td>
                          <td className={appearance === "alpha" ? "px-3 py-2 text-sm text-v7-ink-soft" : "px-3 py-2 text-sm text-gray-700 dark:text-gray-300"}>
                            {user.name || "-"}
                          </td>
                          <td className="px-3 py-2 text-sm">
                            {user.isValid ? (
                              <span className={appearance === "alpha" ? "flex items-center text-v7-success" : "flex items-center text-green-600 dark:text-green-500"}>
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Valid
                              </span>
                            ) : (
                              <span className={appearance === "alpha" ? "flex items-center text-v7-rust" : "flex items-center text-red-600 dark:text-red-500"}>
                                <AlertCircle className="w-4 h-4 mr-1" />
                                {user.error}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {parseResult.users.length > 10 && (
                    <div className={appearance === "alpha" ? "bg-v7-paper px-3 py-2 text-center text-sm text-v7-ink/60" : "bg-gray-50 px-3 py-2 text-center text-sm text-gray-400 dark:bg-rp-surface-sunken dark:text-gray-500"}>
                      ... and {parseResult.users.length - 10} more rows
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Provider Selection */}
            <div className="mb-6">
              <label className={appearance === "alpha" ? "mb-2 block text-sm font-medium text-v7-ink-soft" : "mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300"}>
                Provider *
              </label>
              {loadingProviders ? (
                <div className="flex items-center space-x-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className={appearance === "alpha" ? "text-sm text-v7-ink/60" : "text-sm text-gray-500 dark:text-gray-400"}>Loading providers...</span>
                </div>
              ) : (
                <select
                  aria-label="Provider"
                  value={selectedProviderId || ""}
                  onChange={(e) => setSelectedProviderId(e.target.value ? Number(e.target.value) : null)}
                  disabled={lockProvider || submitting || !!submitSuccess}
                  className={appearance === "alpha" ? "w-full rounded-v7-panel border border-v7-ink/15 bg-v7-white px-3 py-2 text-sm text-v7-ink transition-all focus:border-v7-bronze/30 focus:outline-none focus:ring-1 focus:ring-v7-bronze-dark disabled:cursor-not-allowed disabled:opacity-60" : "w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 transition-all focus:border-rp-amber focus:outline-none focus:ring-1 focus:ring-rp-amber disabled:cursor-not-allowed disabled:opacity-60 dark:border-rp-separator dark:bg-rp-surface dark:text-white"}
                >
                  <option value="">Select a provider</option>
                  {providers.map((provider) => (
                    <option key={provider.id} value={provider.id}>
                      {provider.name}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Roster Sync Preview */}
            {selectedProviderId && (
              <div className={appearance === "alpha" ? "mb-6 rounded-v7-panel border border-v7-ink/15 bg-v7-paper p-4" : "mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-rp-separator dark:bg-rp-surface"}>
                <div className="flex items-start gap-3">
                  <AlertCircle className={appearance === "alpha" ? "mt-0.5 h-5 w-5 flex-shrink-0 text-v7-bronze-dark" : "mt-0.5 h-5 w-5 flex-shrink-0 text-rp-amber"} />
                  <div className="min-w-0 flex-1">
                    <p className={appearance === "alpha" ? "text-sm font-semibold text-v7-ink" : "text-sm font-semibold text-gray-900 dark:text-white"}>
                      Authoritative roster sync
                    </p>
                    <p className={appearance === "alpha" ? "mt-1 text-sm text-v7-ink-soft" : "mt-1 text-sm text-gray-600 dark:text-gray-300"}>
                      The CSV becomes this provider&apos;s external roster. Current subscribers omitted from the CSV will be removed or downgraded if active strategies exist.
                    </p>

                    {loadingRoster ? (
                      <div className={appearance === "alpha" ? "mt-3 flex items-center gap-2 text-sm text-v7-ink/60" : "mt-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400"}>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading current roster...
                      </div>
                    ) : rosterError ? (
                      <p className={appearance === "alpha" ? "mt-3 text-sm text-v7-rust" : "mt-3 text-sm text-red-600 dark:text-red-400"}>
                        {rosterError}
                      </p>
                    ) : validUsers.length === 0 ? (
                      <p className={appearance === "alpha" ? "mt-3 text-sm text-v7-ink/60" : "mt-3 text-sm text-gray-500 dark:text-gray-400"}>
                        Add CSV rows to preview omitted subscribers before syncing.
                      </p>
                    ) : omittedSubscribers.length === 0 ? (
                      <p className={appearance === "alpha" ? "mt-3 text-sm text-v7-success" : "mt-3 text-sm text-green-600 dark:text-green-500"}>
                        No current subscribers are omitted from this CSV.
                      </p>
                    ) : (
                      <>
                        <p className={appearance === "alpha" ? "mt-3 text-sm font-medium text-v7-rust" : "mt-3 text-sm font-medium text-red-600 dark:text-red-400"}>
                          {omittedSubscribers.length} current subscriber{omittedSubscribers.length !== 1 ? "s" : ""} omitted from this CSV
                        </p>
                        <div className={appearance === "alpha" ? "mt-2 max-h-28 overflow-y-auto rounded-v7-panel border border-v7-ink/15 bg-v7-white" : "mt-2 max-h-28 overflow-y-auto rounded-lg border border-gray-200 bg-white dark:border-rp-separator dark:bg-black"}>
                          {omittedSubscribers.slice(0, 8).map((subscription) => (
                            <div
                              key={subscription.id}
                              className={appearance === "alpha" ? "border-b border-v7-ink/15 px-3 py-2 last:border-b-0" : "border-b border-gray-200 px-3 py-2 last:border-b-0 dark:border-rp-separator"}
                            >
                              <p className={appearance === "alpha" ? "truncate font-mono text-sm text-v7-ink" : "truncate font-mono text-sm text-gray-900 dark:text-white"}>
                                {subscription.subscriptionUser.email}
                              </p>
                              {subscription.subscriptionUser.name && (
                                <p className={appearance === "alpha" ? "truncate text-xs text-v7-ink/60" : "truncate text-xs text-gray-500 dark:text-gray-400"}>
                                  {subscription.subscriptionUser.name}
                                </p>
                              )}
                            </div>
                          ))}
                        </div>
                        {omittedSubscribers.length > 8 && (
                          <p className={appearance === "alpha" ? "mt-2 text-xs text-v7-ink/60" : "mt-2 text-xs text-gray-500 dark:text-gray-400"}>
                            + {omittedSubscribers.length - 8} more omitted subscriber{omittedSubscribers.length - 8 !== 1 ? "s" : ""}
                          </p>
                        )}
                        <label className="mt-3 flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={confirmRosterSync}
                            onChange={(event) => setConfirmRosterSync(event.target.checked)}
                            className={appearance === "alpha" ? "mt-0.5 rounded-v7-control border-v7-ink/25 text-v7-bronze-dark focus:ring-v7-bronze-dark" : "mt-0.5 rounded border-gray-300 text-rp-amber focus:ring-rp-amber dark:border-rp-separator"}
                          />
                          <span className={appearance === "alpha" ? "text-sm text-v7-ink-soft" : "text-sm text-gray-700 dark:text-gray-300"}>
                            I understand omitted current subscribers will be removed or downgraded during this sync.
                          </span>
                        </label>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {submitError && (
              <div className={appearance === "alpha" ? "mb-4 flex items-center rounded-v7-panel border border-v7-rust/30 bg-v7-rust/5 p-3 text-sm text-v7-rust" : "mb-4 flex items-center rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"}>
                <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
                {submitError}
              </div>
            )}
    </AdminModalShell>
  );
};

export default BulkAddUsersModal;
